import { useModal } from '@ebay/nice-modal-react';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useState } from 'react';

import {
  CONNECTED_QR_AUTH_STATUSES,
  TERMINAL_QR_AUTH_STATUSES,
  useDisconnectPage,
  useGetQrAuth,
} from '../api';
import { QrAuthStatus } from '../types';

import { toasts } from '@/utils/toasts';

const LINK_WAIT_TIMEOUT_MS = 60_000;
const SUCCESS_CLOSE_DELAY_MS = 1600;
const DISCONNECT_TOAST_ID = 'tg-qr-auth-disconnect';

const FAILURE_MESSAGES = {
  refused: 'Telegram refused the QR login. Close this window and try again.',
  poll: 'Lost contact with the server while waiting for the QR code. Close this window and try again.',
  timeout: 'Telegram has not returned a QR code in a minute. Close this window and try again.',
};

export function getFailureMessage({
  isPollError,
  isLinkTimedOut,
  status,
}: {
  isPollError: boolean;
  isLinkTimedOut: boolean;
  status?: QrAuthStatus;
}) {
  if (status === 'failed') {
    return FAILURE_MESSAGES.refused;
  }
  if (isPollError) {
    return FAILURE_MESSAGES.poll;
  }
  if (isLinkTimedOut) {
    return FAILURE_MESSAGES.timeout;
  }
  return null;
}

/**
 * Drives the Telegram QR login lifecycle for an already-started connect: polls
 * `qr-auth` until a terminal status, gives up if Telegram never returns a link,
 * auto-closes shortly after `ready`, and releases the server-side session by
 * disconnecting the page when the manager closes before the login went through.
 */
export function useQrAuth({ pageId }: { pageId: string }) {
  const { visible: isOpened, remove: removeModal } = useModal();
  // Hook-level callbacks: `closeModal` unmounts the modal right after `mutate`,
  // and mutate-level callbacks are dropped once the observer unsubscribes.
  const { mutate: disconnectPage } = useDisconnectPage({
    config: {
      onMutate: () => toasts.loadingToast.loading(DISCONNECT_TOAST_ID, 'Отключение страницы…'),
      onSuccess: () => notifications.hide(DISCONNECT_TOAST_ID),
      onError: () =>
        toasts.loadingToast.error(
          DISCONNECT_TOAST_ID,
          'Не удалось отключить страницу. Telegram-сессия могла остаться активной.'
        ),
    },
  });

  const [isLinkTimedOut, setIsLinkTimedOut] = useState(false);

  const { data, isError: isPollError } = useGetQrAuth({
    pageId,
    config: { enabled: !isLinkTimedOut },
  });

  const qrAuth = data?.qrAuth ?? null;
  const status = qrAuth?.status;
  const isLinkReceived = Boolean(qrAuth?.link);
  const isTerminal = Boolean(status && TERMINAL_QR_AUTH_STATUSES.includes(status));
  const isPollingStopped = isLinkTimedOut || isTerminal || isPollError;

  useEffect(() => {
    if (isLinkReceived || isPollingStopped) {
      return;
    }
    const timeoutId = setTimeout(() => setIsLinkTimedOut(true), LINK_WAIT_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isLinkReceived, isPollingStopped]);

  const closeModal = useCallback(
    ({ isPageConnected }: { isPageConnected: boolean }) => {
      if (!isPageConnected) {
        disconnectPage({ pageId, platform: 'telegram' });
      }
      removeModal();
    },
    [disconnectPage, removeModal, pageId]
  );

  useEffect(() => {
    if (status !== 'ready') {
      return;
    }
    const timeoutId = setTimeout(
      () => closeModal({ isPageConnected: true }),
      SUCCESS_CLOSE_DELAY_MS
    );
    return () => clearTimeout(timeoutId);
  }, [status, closeModal]);

  const handleClose = useCallback(() => {
    closeModal({
      isPageConnected: Boolean(status && CONNECTED_QR_AUTH_STATUSES.includes(status)),
    });
  }, [closeModal, status]);

  const failureMessage = getFailureMessage({ isPollError, isLinkTimedOut, status });

  return { qrAuth, failureMessage, isOpened, handleClose };
}
